/**
 * Small, dependency-free motion helpers for the product shell.
 *
 * Business state never flows through here. These helpers only add or remove
 * presentation classes, and both become inert when reduced motion is enabled.
 */

function reducedMotionPreferred() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
}

/** Reveal non-essential sections once, as they approach the viewport. */
export function setupScrollReveals(root, targets = []) {
  if (reducedMotionPreferred() || typeof IntersectionObserver !== 'function') return () => {};

  const revealTargets = targets.filter(Boolean);
  root.classList.add('has-scroll-reveals');
  revealTargets.forEach((target, index) => {
    target.classList.add('reveal-on-scroll');
    target.style.setProperty('--reveal-index', String(index));
  });

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    }
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

  revealTargets.forEach((target) => observer.observe(target));
  const fallback = window.setTimeout(() => {
    revealTargets.forEach((target) => target.classList.add('is-revealed'));
    observer.disconnect();
  }, 1400);

  return () => {
    window.clearTimeout(fallback);
    observer.disconnect();
    root.classList.remove('has-scroll-reveals');
    revealTargets.forEach((target) => target.classList.remove('reveal-on-scroll', 'is-revealed'));
  };
}

/** Restart a CSS entrance sequence without forcing synchronous layout. */
export function restartAnimationClass(node, className) {
  if (!node || reducedMotionPreferred()) return () => {};
  node.classList.remove(className);
  const frame = window.requestAnimationFrame(() => node.classList.add(className));
  return () => window.cancelAnimationFrame?.(frame);
}
